/* eslint-disable @typescript-eslint/no-explicit-any */

import { Skeleton } from "@/components/ui/skeleton";
import { useGalleryCtx } from "@/contexts/gallery";
import { cn } from "@/lib/utils";
import { ArrowLeftCircle, ArrowRightCircle } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import "swiper/css";
import "swiper/css/effect-fade";
import { Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper/types";
import { filter, GalleryItmes, SectionProps } from "../types/common";
import Card from "./card";
import { MainFilter, YearFilter, YearQuery } from "./filter";
import { LoadingCard } from "./loading-card";

type GallerSlideProps = {
  loading: boolean;
  items: GalleryItmes[];
  setPageNo: Dispatch<SetStateAction<number>>;
  loadingMore: boolean;
  data: SectionProps;
  year?: filter;
  type?: filter;
  setYear?: (year: filter) => void;
  setType?: (type: filter) => void;
  hasMainFilter?: boolean;
  hasYearFilter?: boolean;
  yearQuery: YearQuery;
};

export default function GallerySliderMobile({
  loading,
  items,
  setPageNo,
  loadingMore,
  data,
  year,
  type,
  setYear,
  setType,
  hasMainFilter = true,
  hasYearFilter = true,
  yearQuery,
}: GallerSlideProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType>();
  const [isSwiperReady, setIsSwiperReady] = useState(false); // Track swiper readiness
  const { setCurrentGalleryItems, setCurrentGalleryItem } = useGalleryCtx();

  function openExpandedView(index: number) {
    setCurrentGalleryItem(index);
    setCurrentGalleryItems(items);
  }

  function handleReachEnd() {
    setPageNo((prevPageNo: number) => prevPageNo + 1);
  }

  return (
    <>
      <NavigationButtons
        yearQuery={yearQuery}
        year={year}
        type={type}
        setYear={setYear}
        setType={setType}
        swiper={swiperInstance}
        data={data}
        hasMainFilter={hasMainFilter}
        hasYearFilter={hasYearFilter}
      />

      {!isSwiperReady && (
        <div className="flex w-full gap-4">
          {Array.from({ length: 3 }).map((_, idx) => {
            return (
              <div key={idx} className="basis-11/12 sm:basis-9/12">
                <Skeleton className="h-[200px] w-[300px] rounded-2xl" />
              </div>
            );
          })}
        </div>
      )}

      <Swiper
        modules={[Virtual]}
        spaceBetween={12}
        slidesPerView={1}
        breakpoints={{
          768: {
            slidesPerView: 2.5,
          },
          640: {
            slidesPerView: 2,
          },
          480: {
            slidesPerView: 1.4,
          },
        }}
        initialSlide={0}
        onReachEnd={handleReachEnd}
        virtual
        observer={true}
        observeParents={true}
        className={`!overflow-visible transition-opacity duration-300 ${isSwiperReady ? "opacity-100" : "opacity-0"}`}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          setIsSwiperReady(true);
        }}
      >
        {items.map((item: any, index: number) => (
          <SwiperSlide key={index}>
            <Card
              loading={loading}
              item={item}
              key={item?.title}
              onClick={() => openExpandedView(index)}
            />
          </SwiperSlide>
        ))}

        {loadingMore && (
          <SwiperSlide key="loading">
            <LoadingCard />
          </SwiperSlide>
        )}
      </Swiper>
    </>
  );
}

type NavigationButtons = {
  swiper: SwiperType | undefined;
  data: SectionProps;
  year: filter | undefined;
  type: filter | undefined;
  setYear: ((year: filter) => void) | undefined;
  setType: ((type: filter) => void) | undefined;
  hasMainFilter: boolean;
  hasYearFilter: boolean;
  yearQuery: YearQuery;
};

function NavigationButtons({
  year,
  type,
  setYear,
  setType,
  swiper,
  data,
  hasMainFilter,
  hasYearFilter,
  yearQuery,
}: NavigationButtons) {
  const [isBeginning, setIsBeginning] = useState(true);

  useEffect(() => {
    if (!swiper) return;
    const handleSwiperChange = () => {
      setIsBeginning(swiper.isBeginning);
    };

    swiper.on("slideChange", handleSwiperChange);

    return () => {
      swiper.off("slideChange", handleSwiperChange);
    };
  }, [swiper]);

  return (
    <div className="mb-6 ltr:-top-44 ltr:right-0 rtl:left-0 rtl:top-0">
      <h1 className="mb-8 text-[38px] text-secondary ltr:font-playfair ltr:tracking-[-1.44px]">
        {data.title}
      </h1>
      <div className="flex items-center justify-between">
        <div className="space-x-1 xs:space-x-2 lg:space-x-4">
          {hasMainFilter && type && setType && (
            <MainFilter options={data.dropdown_text.values} type={type} setType={setType} />
          )}
          {hasYearFilter && year && setYear && (
            <YearFilter
              yearQuery={yearQuery}
              years={data.year_filter_text.years}
              year={year}
              setYear={setYear}
            />
          )}
        </div>
        <div className="flex gap-1">
          <ArrowLeftCircle
            strokeWidth={0.5}
            className={cn(
              "size-[36px] cursor-pointer text-secondary xs:size-[42px] sm:size-[58px]",
              isBeginning ? "cursor-not-allowed opacity-40" : "",
              "ltr:rotate-0 rtl:rotate-180"
            )}
            onClick={() => !isBeginning && swiper?.slidePrev()}
          />
          <ArrowRightCircle
            strokeWidth={0.5}
            className={cn(
              "size-[36px] cursor-pointer text-secondary xs:size-[42px] sm:size-[58px]",
              "ltr:rotate-0 rtl:rotate-180"
            )}
            onClick={() => swiper?.slideNext()}
          />
        </div>
      </div>
    </div>
  );
}
